import apiClient from "@/libs/api";
import config from "@/config";
import { getAvailableProviders } from "./provider-config";

// 支付服务的基类
class PaymentService {
  async createCheckout(options) {
    throw new Error('Must implement createCheckout');
  }
  
  getPaymentMethodIcon() {
    return null;
  }
  
  getPaymentMethodName() {
    return '';
  }
}

// Stripe支付实现
class StripePayment extends PaymentService {
  async createCheckout({ priceId, mode, successUrl, cancelUrl, user, paymentType, plan, isYearly }) {
    // 如果传入了plan对象，需要处理价格ID结构差异
    let finalPriceId = priceId;
    
    if (plan && !priceId) {
      const environment = process.env.NODE_ENV === 'production' ? 'production' : 'development';
      
      if (plan.isOneTime) {
        // 一次性积分包：从 config.creditPacks.packs 中查找
        const creditPack = config.creditPacks.packs.find(pack => pack.name === plan.name);
        if (creditPack && creditPack.priceId?.stripe?.[environment]) {
          finalPriceId = creditPack.priceId.stripe[environment];
        } else {
          // 如果在creditPacks中没找到，尝试使用plan对象自身的priceId
          finalPriceId = plan.priceId?.stripe?.[environment];
        }
      } else {
        // 订阅计划：使用 prices.stripe[period][environment] 结构
        finalPriceId = plan.prices?.stripe?.[isYearly ? 'yearly' : 'monthly']?.[environment];
      }
      
      if (!finalPriceId) {
        throw new Error(`Price ID not found for plan: ${plan.name}, environment: ${environment}, isOneTime: ${plan.isOneTime}`);
      }
    }
    
    const res = await apiClient.post("/stripe/create-checkout", {
      priceId: finalPriceId,
      mode,
      successUrl,
      cancelUrl,
      paymentType
    });
    return res.url;
  }
  
  getPaymentMethodIcon() {
    return "/icons/stripe.svg";
  }
  
  getPaymentMethodName() {
    return "Credit Card";
  }
}

// PayPro支付实现
class PayProPayment extends PaymentService {
  async createCheckout({ priceId, mode, successUrl, cancelUrl, user }) {
    if (!user) {
      throw new Error('User is required for PayPro checkout');
    }

    // 根据环境判断是否使用测试模式
    const isTestMode = process.env.NODE_ENV !== 'production';
    
    // 构建PayPro结账URL
    let checkoutUrl = `https://store.payproglobal.com/checkout?`;
    
    // 添加必要参数
    const params = new URLSearchParams({
      'products[1][id]': priceId,
      'page-template': config.payments.methods.paypro.config.pageTemplate,
      'billing-email': user.email
    });

    // 测试模式参数
    if (isTestMode) {
      params.append('use-test-mode', 'true');
      params.append('secret-key', config.payments.methods.paypro.config.testMode.secretKey);
    }

    checkoutUrl += params.toString();
    
    return checkoutUrl;
  }
  
  getPaymentMethodIcon() {
    return "/icons/paypro.svg";
  }
  
  getPaymentMethodName() {
    return "PayPro";
  }
}

// Creem支付实现
class CreemPayment extends PaymentService {
  async createCheckout({ priceId, mode, successUrl, cancelUrl, user, paymentType }) {
    if (!user) {
      throw new Error('User is required for Creem checkout');
    }

    const res = await apiClient.post("/creem/checkout", {
      paymentType: paymentType
    });
    return res.url;
  }
  
  getPaymentMethodIcon() {
    return "/icons/creem.svg";
  }
  
  getPaymentMethodName() {
    return "Creem";
  }
}

// Antom支付实现
class AntomPayment extends PaymentService {
  async createCheckout({ priceId, mode, successUrl, cancelUrl }) {
    const res = await apiClient.post("/antom/checkout", {
      planId: priceId,
      type: mode,
      success_url: successUrl,
      cancel_url: cancelUrl
    });
    return res.url;
  }
  
  getPaymentMethodIcon() {
    return "/icons/antom.svg";
  }
  
  getPaymentMethodName() {
    return "Antom Pay";
  }
}

class AnywayPayment extends PaymentService {
  async createCheckout({ paymentType }) {
    const res = await apiClient.post("/anyway/checkout", { paymentType });
    return res.url;
  }

  getPaymentMethodIcon() { return "/icons/anyway.svg"; }
  getPaymentMethodName() { return "Anyway"; }
}

// 支付服务工厂
const paymentServices = {
  anyway: new AnywayPayment(),
  stripe: new StripePayment(),
  paypro: new PayProPayment(),
  creem: new CreemPayment(),
  antom: new AntomPayment()
};

export const getPaymentService = (provider) => {
  const service = paymentServices[provider];
  if (!service) {
    throw new Error(`Payment provider ${provider} not supported`);
  }
  return service;
};

// Only show channels configured for the selected product and billing period.
export const getAvailablePaymentMethods = (plan, isYearly = false) =>
  getAvailableProviders(config, plan, isYearly)
    .filter(({ id }) => paymentServices[id]);
